import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
    Alert,
    Box,
    Button,
    IconButton,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import * as React from 'react';

import { formatTs, ToneChip } from '../components';
import type { Integration } from '../integrationTypes';
import AccountEditor, { CredentialsBlock } from './AccountEditor';
import AccountRows from './AccountRows';
import useCheckNow from './useCheckNow';
import FactStrip from './FactStrip';
import { useIntegrations } from './IntegrationsContext';
import { statusSentence } from './integrationLabels';
import { CardSection, DiscardPrompt } from './parts';
import RemoveDialog from './RemoveDialog';
import SettingsEditor from './SettingsEditor';
import TagCategoriesSection from './TagCategoriesSection';
import { VerdictChip } from './Verdict';
import WebhookEndpoints from './WebhookEndpoints';

/** What Remove says (plan §1 Remove): what stops, what stays, and the vendor side. */
export const removeLines = (connection: Integration, environment: string): string[] => {
    const name = connection.name;
    const lines = [`Webhooks and imports from ${name} stop at once. Trips and bookings already imported stay.`];
    if (connection.accounts.length > 1) {
        lines.push(`All ${connection.accounts.length} accounts go with it.`);
    }
    if (connection.vendor_cleanup === 'uninstall') {
        lines.push(environment === 'sandbox'
            ? `A sandbox changes nothing at ${name}: remove the webhooks there yourself.`
            : `Its webhooks are uninstalled at ${name} first. If ${name} refuses, nothing is removed.`);
    } else if (connection.vendor_cleanup === 'manual') {
        lines.push(`Remove the webhook settings in ${name} too: it keeps sending until you do.`);
    }
    return lines;
};

/** The card's header (plan §1.1): name · verdict · what else is true of it · [Check now] · [⋯] with Remove. */
export const CardHeader = ({ connection, actions, onRemove }: {
    connection: Integration;
    actions?: React.ReactNode;
    onRemove?: () => void;
}) => {
    const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
    const titleId = `connection-${connection.id}-title`;
    return (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ rowGap: 1, mb: 0.75 }}>
            <Typography id={titleId} variant="subtitle1" component="h2" sx={{ fontWeight: 500 }}>{connection.name}</Typography>
            <VerdictChip status={connection.status} />
            {connection.inherited_from && <ToneChip label={`Managed by ${connection.inherited_from.name}`} tone="neutral" />}
            <Box sx={{ flexGrow: 1 }} />
            {actions}
            {onRemove && (
                <>
                    <IconButton size="small" aria-label={`More actions for ${connection.name}`} aria-haspopup="menu" onClick={(ev) => setAnchor(ev.currentTarget)}>
                        <MoreVertIcon fontSize="small" />
                    </IconButton>
                    <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)} sx={{ zIndex: 10002 }}>
                        <MenuItem sx={{ color: 'error.main' }} onClick={() => { setAnchor(null); onRemove(); }}>Remove {connection.name}…</MenuItem>
                    </Menu>
                </>
            )}
        </Stack>
    );
};

/** Settings (plan §1.6): a collapsed summary; [Edit] opens the in-card form. Schema-less systems say who
 *  manages them; an inherited section reads the parent's values. */
export const SettingsSection = ({ connection, canEdit }: { connection: Integration; canEdit: boolean }) => {
    const { api, replace } = useIntegrations();
    const { enqueueSnackbar } = useSnackbar();
    const [open, setOpen] = React.useState(false);
    const [dirty, setDirty] = React.useState(0);
    const [asking, setAsking] = React.useState(false);
    const settings = connection.settings;
    if (!settings) {
        return connection.managed_by === 'curated_planet' ? (
            <CardSection title="Settings" testId="settings">
                <Typography variant="body2" color="text.secondary" data-testid="managed-by-cp">
                    Settings are managed by Curated Planet. Ask support to change how {connection.name} is imported.
                </Typography>
            </CardSection>
        ) : null;
    }
    if (!Object.keys(settings.values).length) {
        return null;
    }
    const save = async (patch: Record<string, any>) => {
        const updated = await api.saveSettings(connection.id, patch);
        replace(updated);
        enqueueSnackbar(`Settings of ${connection.name} saved`, { variant: 'success' });
    };
    const summary = `${settings.total} ${settings.total === 1 ? 'setting' : 'settings'} · ${settings.changed} changed`;
    const close = () => {
        setAsking(false);
        setDirty(0);
        setOpen(false);
    };
    const toggle = () => {
        if (open && dirty > 0) {
            setAsking(true);
        } else if (open) {
            close();
        } else {
            setOpen(true);
        }
    };
    return (
        <CardSection
            title="Settings"
            testId="settings"
            actions={(
                <Button size="small" aria-expanded={open} onClick={toggle}>
                    {open ? 'Close' : canEdit ? 'Edit' : 'View'}
                </Button>
            )}
        >
            <Typography variant="body2" color="text.secondary" data-testid="settings-summary">{summary}</Typography>
            {asking && <DiscardPrompt onDiscard={close} onKeep={() => setAsking(false)} />}
            {open && (
                <Box sx={{ mt: 1 }}>
                    <SettingsEditor
                        schema={settings.schema}
                        values={settings.values}
                        level="connection"
                        editable={canEdit}
                        parentName={connection.inherited_from?.name}
                        onSave={save}
                        onClose={close}
                        onDirtyChange={setDirty}
                    />
                </Box>
            )}
        </CardSection>
    );
};

/** One booking system (plan §1 Connection card): status first, then its facts, accounts, webhooks,
 *  settings and tag categories (sdtc WP-G). Every word about the vendor comes from the API, so there is no
 *  per-vendor card. */
const ConnectionCard = ({ connection, justAdded = false, onShowTenant }: {
    connection: Integration;
    justAdded?: boolean;
    /** A franchise parent's user: show another of `licensees` (the inherited card's parent). */
    onShowTenant?: (tenantId: number) => void;
}) => {
    const { permissions, environment, api, reload, noteRemoval, trackedSince } = useIntegrations();
    const { enqueueSnackbar } = useSnackbar();
    const canEdit = permissions.edit && connection.editable;
    const [removing, setRemoving] = React.useState(false);
    const [addingAccount, setAddingAccount] = React.useState(false);
    const checkNow = useCheckNow(connection);
    const single = connection.accounts.length <= 1;
    const account = connection.accounts[0];
    const uninstall = connection.vendor_cleanup === 'uninstall' && environment !== 'sandbox';

    return (
        <Paper
            variant="outlined"
            component="article"
            aria-labelledby={`connection-${connection.id}-title`}
            sx={{ p: { xs: 1.5, sm: 2 } }}
            data-testid={`connection-${connection.id}`}
        >
            <CardHeader
                connection={connection}
                actions={canEdit && connection.can_check && single ? checkNow.button : undefined}
                onRemove={canEdit && connection.can_remove ? () => setRemoving(true) : undefined}
            />
            <Typography variant="body2" data-testid="status-sentence" title={connection.status.at ? formatTs(connection.status.at) : undefined}>
                {statusSentence(connection.status, connection, trackedSince)}
            </Typography>
            {justAdded && (
                <Alert severity="info" sx={{ mt: 1.5 }} data-testid="just-added">
                    {connection.name} is added. Next: set up its webhooks below, so bookings start to arrive.
                </Alert>
            )}
            {checkNow.result}
            {connection.inherited_from && (
                <Alert
                    severity="info"
                    sx={{ mt: 1.5 }}
                    data-testid="inherited"
                    action={onShowTenant ? (
                        <Button color="inherit" size="small" onClick={() => onShowTenant(connection.inherited_from!.id)}>
                            Show {connection.inherited_from.name}
                        </Button>
                    ) : undefined}
                >
                    This company uses {connection.inherited_from.name}&apos;s {connection.name} connection: change it there.
                </Alert>
            )}
            <FactStrip facts={connection.facts} systemName={connection.name} />
            {single && account ? (
                <CardSection
                    title="Credentials"
                    testId="single-account"
                    actions={canEdit && connection.can_add_account && !addingAccount ? (
                        <Button size="small" startIcon={<AddIcon />} onClick={() => setAddingAccount(true)}>Add account</Button>
                    ) : undefined}
                >
                    <CredentialsBlock connection={connection} account={account} canEdit={canEdit} />
                    {addingAccount && <AccountEditor connection={connection} account={null} canEdit={canEdit} onClose={() => setAddingAccount(false)} />}
                </CardSection>
            ) : (
                <AccountRows connection={connection} canEdit={canEdit} />
            )}
            <WebhookEndpoints connection={connection} canEdit={canEdit} />
            <SettingsSection connection={connection} canEdit={canEdit} />
            {connection.kind !== 'fleet' && <TagCategoriesSection connection={connection} />}
            <RemoveDialog
                open={removing}
                title={`Remove ${connection.name}?`}
                lines={removeLines(connection, environment)}
                confirmation={connection.remove_confirmation}
                confirmLabel={`Type ${connection.remove_confirmation} to confirm`}
                actionLabel={uninstall ? 'Uninstall and remove' : 'Remove'}
                onRemove={async (typed) => {
                    noteRemoval(connection.name, await api.remove(connection.id, typed));
                    enqueueSnackbar(`${connection.name} removed`, { variant: 'success' });
                    // Not awaited: the dialog closes first, then the list drops this card.
                    reload();
                }}
                onClose={() => setRemoving(false)}
            />
        </Paper>
    );
};

export default ConnectionCard;
